import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Drawer, Input, Select, InputNumber, Tooltip, Alert, Spin, Switch } from "antd";
// NAVIGATION
import { useLocation } from "react-router-dom";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// FONT AWESOME LIBRARY AND ICONS
import { faBuilding, faCircleInfo, faEthernet, faGears, faFileCode } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateOpenModalCreateEditBank, updateBank } from "../../../redux/banks/bankSlice";
import ConfigurationFilesAssignConfiguration from "../../configurationManagement/configurationFiles/ConfigurationFilesAssignConfiguration";
import { usePostBankMutation, usePutBankMutation } from "../../../redux/banks/bankAPI";
import { usePostLiftMutation } from "../../../redux/lifts/liftAPI";
import { useLazyGetServerQuery } from "../../../redux/servers/serverAPI";
// COMPONENTS
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import SelectOrganization from "../../components/select/SelectOrganization";
import SelectServerByOrganization from "../../components/select/SelectServerByOrganization";
import AdaptersAssignAdapter from "../adapters/AdaptersAssignAdapter";
import GlobalAlert2 from "../../home/GlobalAlert2";
import ButtonClose from "../../components/buttons/ButtonClose";
import ButtonSave from "../../components/buttons/ButtonSave";
import CdoBacnetPage from "../adapters/CDO/BACNET/CdoBacnetPage";
// INTERFACES
import { RootState } from "../../../app/store";
import { BankInterface } from "../../../interfaces/Bank.interface";
import { ChannelInterface } from "../../../interfaces/Channel.interface";
import { FloorsAndDirectionDataInterface } from "../../../interfaces/Configurations.interface";
import { BacnetConfigEntryInterface } from "../../../interfaces/AdapterCdoBacnet.interface";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// CONSTANTS
import { BLANK_BANK } from "../../../constants/bank";

const DEFAULT_BACNET_CONFIG_ENTRY: BacnetConfigEntryInterface = {
	bank: {},
	bank_id: "",
	descriptions: [],
};

function BanksCreateEditForm() {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");
	const { Option } = Select;
	const location = useLocation();
	const idServer = new URLSearchParams(location.search).get("idServer");
	const BLANK_FLOORS_DIRECTION: FloorsAndDirectionDataInterface = {
		numberOfLifts: 0,
		numberOfStops: 2,
		maxNumberOfStops: 0,
		numberOfFloorsBelowLobby: 0,
		configurationId: "",
		adapterId: "",
	};

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [bank, setBank] = useState({ ...BLANK_BANK });
	const [idOrganization, setIdOrganization] = useState<string>("");
	const [channels, setChannels] = useState<ChannelInterface[]>([]);
	const [channelType, setChannelType] = useState<string>("");
	const [dataBlocks, setDataBlocks] = useState([]);
	const [floorsAndDirectionData, setFloorsAndDirectionData] =
		useState<FloorsAndDirectionDataInterface>(BLANK_FLOORS_DIRECTION);
	const [showLoading, setShowLoading] = useState<boolean>(false);
	const [bacnetConfig, setBacnetConfig] = useState<BacnetConfigEntryInterface>({ ...DEFAULT_BACNET_CONFIG_ENTRY });

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { openModalCreateEditBank, formAction, bank: selectedBank } = useSelector((state: RootState) => state.bank);
	const { theme, userLogged, globalOrganizationId } = useSelector((state: RootState) => state.home);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [triggerGetServer, { isLoading: isLoadingGetServer }] = useLazyGetServerQuery();

	const [
		triggerPostBank,
		{
			isSuccess: isSuccessPostBank,
			isLoading: isLoadingPostBank,
			isError: isErrorPostBank,
			error: errorPostBank,
			reset: resetPostBank,
		},
	] = usePostBankMutation();

	const [
		triggerPutBank,
		{
			isSuccess: isSuccessPutBank,
			isError: isErrorPutBank,
			error: errorPutBank,
			isLoading: isLoadingPutBank,
			reset: resetPutBank,
		},
	] = usePutBankMutation();

	const [triggerPostLift, { isSuccess: isSuccessPostLift, isError: isErrorPostLift, reset: resetPostLift }] =
		usePostLiftMutation();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const createAutomaticallyLifts = async (bankId: string, bankType: string, numberOfLifts: number) => {
		const token = await GETJwtToken();
		const promises = [];
		for (let i = 1; i <= numberOfLifts; i += 1) {
			const BODY = {
				token,
				body: {
					bank_id: bankId,
					offsets: [],
					type: bankType,
					name: `Car ${i}`,
					position: i,
					direction: "",
					control_panel: JSON.stringify({
						carcall: {
							code: "cc",
							map: [
								{
									offset: "3c",
									target_bit: "01",
									new_bit: "10",
								},
							],
						},
						rear_carcall: {
							code: "rcc",
							map: [
								{
									offset: "3c",
									new_byte: "1a",
								},
							],
						},
						close_door: {
							code: "cd",
							map: [
								{
									offset: "3c",
									target_bit: "01",
									new_bit: "10",
								},
							],
						},
						open_door: {
							code: "od",
							map: [
								{
									offset: "3c",
									new_byte: "1a",
								},
							],
						},
						hall_call: {
							code: "hc",
							map: [
								{
									offset: "3c",
									target_bit: "01",
									new_bit: "10",
								},
							],
						},
						secure: {
							code: "sec",
							map: [
								{
									offset: "3c",
									new_byte: "10",
								},
							],
						},
					}),
				},
			};
			promises.push(triggerPostLift(BODY).unwrap());
		}
	};

	const onClickSave = async () => {
		const token = await GETJwtToken();
		const BODY = { ...bank };
		if (channelType === "SERIAL" || channelType === "TCP" || channelType === "TCP_BOT") {
			delete BODY.adapter_id;
		}
		if (channelType === "ADAPTER") {
			delete BODY.configuration_id;
		}
		// Add BACnet configuration
		if (bank.clear_bacnet_config) {
			BODY.bacnet_config_raw = {
				descriptions: [],
				bank: {},
				bank_id: ""
			}
		} else {
			BODY.bacnet_config_raw = bacnetConfig;
		}

		if (formAction === "CREATE") {
			const copy: any = structuredClone(BODY);
			copy.floor_count = copy.stops;
			copy.bacnet_config_raw = null;
			const response = await triggerPostBank({ token, body: copy }).unwrap();
			if (response && response.data && response.data.id) {
				createAutomaticallyLifts(response.data.id, BODY.type, BODY.number_of_lifts);
			}
		} else {
			const copy = structuredClone(BODY);
			delete copy.data_blocks;
			copy.lifts = [];
			triggerPutBank({ token, body: copy, id: bank.id });
		}
	};

	const onChangeShowLoading = () => {
		setShowLoading(true);
		setTimeout(() => {
			setShowLoading(false);
		}, 300);
	};

	const resetData = () => {
		setBank({ ...BLANK_BANK });
		setIdOrganization("");
		setChannels([]);
		setChannelType("");
		setDataBlocks([]);
		setFloorsAndDirectionData(BLANK_FLOORS_DIRECTION);
		setBacnetConfig(DEFAULT_BACNET_CONFIG_ENTRY);
		resetPostBank();
		resetPutBank();
		resetPostLift();
		dispatch(updateBank({ ...BLANK_BANK }));
	};

	const onClickClose = () => {
		resetData();
		dispatch(updateOpenModalCreateEditBank(false));
	};

	const onChangeFormValue = (newValue: string | number, variableName: string) => {
		const copy: BankInterface = structuredClone(bank);
		if (variableName === "name" && typeof newValue === "string") {
			copy.name = newValue;
		} else if (variableName === "type" && typeof newValue === "string") {
			copy.type = newValue;
		} else if (variableName === "board" && typeof newValue === "number") {
			copy.board = newValue;
		} else if (variableName === "stops" && typeof newValue === "number") {
			copy.stops = newValue;
		} else if (variableName === "floor_count" && typeof newValue === "number") {
			copy.floor_count = newValue;
		} else if (variableName === "number_of_lifts" && typeof newValue === "number") {
			copy.number_of_lifts = newValue;
		} else if (variableName === "channel_id" && typeof newValue === "string") {
			copy.channel_id = newValue;
		} else if (variableName === "logical_port" && typeof newValue === "number") {
			copy.logical_port = newValue;
		}
		setBank(copy);
	};

	const onChangeClearBacnetConfig = (checked: boolean) => {
		const copy: BankInterface = structuredClone(bank);
		copy.clear_bacnet_config = checked;
		setBank(copy);
		if (checked) {
			setBacnetConfig(DEFAULT_BACNET_CONFIG_ENTRY);
		}
	};

	const onChangeIdOrganization = (newValue: string) => {
		setIdOrganization(newValue);
		const copy: BankInterface = structuredClone(bank);
		copy.server_id = "";
		copy.channel_id = "";
		setBank(copy);
	};

	const onChangeServer = (server_: any) => {
		const copy: any = structuredClone(bank);
		copy.server_id = server_.id;
		copy.channel_id = "";
		setBank(copy);
		if (server_.channels) {
			setChannels(server_.channels);
		}
	};

	const disableSaveButton = () => {
		if (bank.name === "" || bank.channel_id === "" || bank.server_id === "") {
			return true;
		}
		return false;
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		if (openModalCreateEditBank) {
			onChangeShowLoading();
			if (formAction === "EDIT") {
				const copy: BankInterface = structuredClone(selectedBank);
				copy.clear_bacnet_config = copy.clear_bacnet_config ?? false;
				setBank(copy);
				// Set channel type from selectedBank
				if (selectedBank.channel && selectedBank.channel.type) {
					setChannelType(selectedBank.channel.type);
				}
				// Load BACnet configuration if exists
				if (copy.bacnet_config_raw) {
					const bacnetConfigWithBankId = {
						...copy.bacnet_config_raw,
						bank_id: copy.id,
					};
					setBacnetConfig(bacnetConfigWithBankId);
				} else {
					setBacnetConfig({
						...DEFAULT_BACNET_CONFIG_ENTRY,
						bank_id: copy.id,
					});
				}
				if (copy.server_id) {
					triggerGetServer(copy.server_id)
						.unwrap()
						.then((response: any) => {
							if (response && response.data) {
								setIdOrganization(response.data.organization_id);
								setChannels(response.data.channels);
							}
						});
				}
			}
		}
	}, [openModalCreateEditBank]);

	useEffect(() => {
		if (bank.channel_id !== "") {
			const index = channels.findIndex((x: any) => x.id === bank.channel_id);
			if (index >= 0) {
				setChannelType(channels[index].type);
			}
		}
	}, [bank.channel_id]);

	useEffect(() => {
		if (dataBlocks) {
			const copy: BankInterface = structuredClone(bank);
			copy.data_blocks = dataBlocks;
			setBank(copy);
		}
	}, [dataBlocks]);

	useEffect(() => {
		if (floorsAndDirectionData) {
			const copy: BankInterface = structuredClone(bank);
			if (channelType !== "ADAPTER") {
				copy.floor_below_lobby = floorsAndDirectionData.numberOfFloorsBelowLobby;
				copy.stops = floorsAndDirectionData.numberOfStops;
				copy.number_of_lifts = floorsAndDirectionData.numberOfLifts;
				copy.configuration_id = floorsAndDirectionData.configurationId;
			} else {
				copy.floor_below_lobby = floorsAndDirectionData.numberOfFloorsBelowLobby;
				copy.stops = floorsAndDirectionData.numberOfStops;
				copy.number_of_lifts = floorsAndDirectionData.numberOfLifts;
				copy.adapter_id = floorsAndDirectionData.adapterId;
			}
			setBank(copy);
		}
	}, [floorsAndDirectionData]);

	useEffect(() => {
		onClickClose();
	}, [isSuccessPostBank, isErrorPostBank, isSuccessPutBank, isErrorPutBank, isSuccessPostLift, isErrorPostLift]);


	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<Drawer
			width='650'
			placement='right'
			onClose={onClickClose}
			closable={false}
			open={openModalCreateEditBank}
			footer={
				<div className='container generalStyles__flex mt-4'>
					<div className='generalStyles__width100 generalStyles__mrFix'>
						<ButtonClose onClick={onClickClose} />
					</div>
					<div className='generalStyles__width100 generalStyles__mlFix'>
						<ButtonSave onClick={onClickSave} disabledButton={disableSaveButton()} isLoading={false} />
					</div>
				</div>
			}
		>
			<Spin spinning={showLoading || isLoadingPostBank || isLoadingPutBank || isLoadingGetServer}>
				{/** ************************************************************* */}
				{/** DIVIDER BANK */}
				<DrawerRowDivider
					icon={faBuilding}
					title={formAction === "CREATE" ? t("edgeManagement.banks.createNewBank") : t("edgeManagement.banks.editBank")}
				/>
				<div className={`drawer__box__${theme}`}>
					{/** NAME */}
					<div className='generalStyles__flex drawer__container'>
						<div>
							<p className='drawer__title'>
								{" "}
								<span className='generalStyles__inputFlexRequired'>*</span>
								{t("general.name")}
							</p>
						</div>
						<div className='generalStyles__width100 drawer__textContainer generalStyles__flex'>
							<Input
								size='small'
								onChange={(value) => {
									onChangeFormValue(value.target.value, "name");
								}}
								value={bank.name}
							/>
							<div className='generalStyles__infoTooltipIconBlank' />
						</div>
					</div>
					{/** TYPE */}
					<div className='generalStyles__flex drawer__container mt-2'>
						<div>
							<p className='drawer__title'>
								{" "}
								<span className='generalStyles__inputFlexRequired'>*</span>
								{t("general.type")}
							</p>
						</div>
						<div className='generalStyles__width100 drawer__textContainer generalStyles__flex'>
							<Select
								value={bank.type}
								onChange={(value) => {
									onChangeFormValue(value, "type");
								}}
								className='generalStyles__width100'
								size='small'
								disabled={formAction === "EDIT"}
							>
								{["ELEVATOR", "ESCALATOR", "MOVINGWALK", "BOT", "GPIO"].map((item: string) => (
									<Option key={item} value={item}>
										{item}
									</Option>
								))}
							</Select>
							<div className='generalStyles__infoTooltipIconBlank' />
						</div>
					</div>
					{/** BOARD */}
					<div className='generalStyles__flex drawer__container mt-2'>
						<div>
							<p className='drawer__title'>
								<span className='generalStyles__inputFlexRequired'>*</span>
								{t("configuration.configurationFiles.board")}
							</p>
						</div>
						<div className='generalStyles__width100 drawer__textContainer generalStyles__flex'>
							<InputNumber
								className='generalStyles__inputNumberFull'
								precision={0}
								value={bank.board}
								onChange={(e: any) => {
									onChangeFormValue(e, "board");
								}}
								max={99999}
								size='small'
							/>
							<div className='generalStyles__infoTooltipIconBlank' />
						</div>
					</div>
					{/** LOGICAL PORT */}
					<div className='generalStyles__flex drawer__container mt-2'>
						<div>
							<p className='drawer__title'>
								<span className='generalStyles__inputFlexRequired'>*</span>
								{t("general.logicalPort")}
							</p>
						</div>
						<div className='generalStyles__width100 drawer__textContainer generalStyles__flex'>
							<InputNumber
								className='generalStyles__inputNumberFull'
								precision={0}
								value={bank.logical_port}
								onChange={(e: any) => {
									onChangeFormValue(e, "logical_port");
								}}
								max={99999}
								size='small'
							/>
							<div className='generalStyles__infoTooltipIconBlank' />
						</div>
					</div>
					{formAction === "EDIT" && (
						<>
							{/** STOPS */}
							<div className='generalStyles__flex drawer__container mt-2'>
								<div>
									<p className='drawer__title'>
										<span className='generalStyles__inputFlexRequired'>*</span>
										{t("configuration.configurationFiles.stops")}
									</p>
								</div>
								<div className='generalStyles__width100 drawer__textContainer generalStyles__flex'>
									<InputNumber
										className='generalStyles__inputNumberFull'
										precision={0}
										value={bank.stops}
										onChange={(e: any) => {
											onChangeFormValue(e, "stops");
										}}
										max={999}
										size='small'
									/>
									<div className='generalStyles__infoTooltipIconBlank' />
								</div>
							</div>
							{/** FLOOR COUNT */}
							<div className='generalStyles__flex drawer__container mt-2'>
								<div>
									<p className='drawer__title'>
										<span className='generalStyles__inputFlexRequired'>*</span>
										{t("configuration.configurationFiles.floors")}
									</p>
								</div>
								<div className='generalStyles__width100 drawer__textContainer generalStyles__flex'>
									<InputNumber
										className='generalStyles__inputNumberFull'
										precision={0}
										value={bank.floor_count}
										onChange={(e: any) => {
											onChangeFormValue(e, "floor_count");
										}}
										max={999}
										size='small'
									/>
									<div className='generalStyles__infoTooltipIconBlank' />
								</div>
							</div>
							{/** NUMBER OF LIFTS */}
							<div className='generalStyles__flex drawer__container mt-2'>
								<div>
									<p className='drawer__title'>
										<span className='generalStyles__inputFlexRequired'>*</span>
										Units
									</p>
								</div>
								<div className='generalStyles__width100 drawer__textContainer generalStyles__flex'>
									<InputNumber
										className='generalStyles__inputNumberFull'
										precision={0}
										value={bank.number_of_lifts}
										onChange={(e: any) => {
											onChangeFormValue(e, "number_of_lifts");
										}}
										max={999}
										size='small'
									/>
									<div className='generalStyles__infoTooltipIconBlank' />
								</div>
							</div>
						</>
					)}
				</div>
				<br />
				{/** ************************************************************* */}
				{/** DIVIDER BANK CONFIGURATION */}
				<DrawerRowDivider icon={faEthernet} title={t("general.channel")} />
				<div className={`drawer__box__${theme}`}>
					{/** ORGANIZATION */}
					<div className='generalStyles__flex drawer__container mt-3'>
						<div>
							<p className='drawer__title'>
								{" "}
								<span className='generalStyles__inputFlexRequired'>*</span>
								{t("userManagement.organization")}
							</p>
						</div>
						<div className='generalStyles__width100 drawer__textContainer generalStyles__flex'>
							<SelectOrganization
								onChangeIdOrganization={onChangeIdOrganization}
								value={idOrganization}
								disabledInput={
									(userLogged && userLogged.org_id && userLogged.org_id !== globalOrganizationId) ||
									(idServer && idServer !== "") ||
									formAction === "EDIT"
								}
							/>
							<div className='generalStyles__infoTooltipIconBlank' />
						</div>
					</div>
					{/** SERVER */}
					<div className='generalStyles__flex drawer__container mt-2'>
						<div>
							<p className='drawer__title'>
								<span className='generalStyles__inputFlexRequired'>*</span>
								{t("edgeManagement.servers.server")}
							</p>
						</div>
						<div className='generalStyles__width100 drawer__textContainer generalStyles__flex'>
							<SelectServerByOrganization
								onChangeServer={onChangeServer}
								value={bank.server_id}
								idOrganization={idOrganization}
								disabledInput={(idServer && idServer !== "") || formAction === "EDIT"}
							/>
							<div className='generalStyles__infoTooltipIconBlank'>
								{bank.server_id === "" && idOrganization === "" && (
									<Tooltip title={t("tooltip.organizationRequired")} placement='left'>
										<FontAwesomeIcon icon={faCircleInfo} className='generalStyles__infoTooltipIcon' />
									</Tooltip>
								)}
							</div>
						</div>
					</div>
					{/** CHANNEL */}
					<div className='generalStyles__flex drawer__container mt-2'>
						<div>
							<p className='drawer__title'>
								<span className='generalStyles__inputFlexRequired'>*</span>
								{t("edgeManagement.channels.channel")}
							</p>
						</div>
						<div className='generalStyles__width100 drawer__textContainer generalStyles__flex'>
							{formAction === "CREATE" ? (
								<Select
									value={bank.channel_id}
									onChange={(value) => {
										onChangeFormValue(value, "channel_id");
									}}
									className='generalStyles__width100'
									size='small'
									disabled={bank.server_id === ""}
									showSearch
									filterOption={(input, option) => {
										const channel = channels.find((ch) => ch.id === option?.value);
										if (!channel) return false;
										const searchText = `${channel.type} ${channel.name}`.toLowerCase();
										return searchText.includes(input.toLowerCase());
									}}
									virtual
									listHeight={256}
								>
									{channels.map((item: ChannelInterface) => (
										<Option key={item.id} value={item.id}>
											<span className='generalStyles__info'>{item.type}</span> <span>{item.name}</span>{" "}
										</Option>
									))}
								</Select>
							) : (
								<Input
									size='small'
									disabled
									value={
										selectedBank && selectedBank.channel && selectedBank.channel.name ? selectedBank.channel.name : "--"
									}
								/>
							)}
							<div className='generalStyles__infoTooltipIconBlank'>
								{bank.channel_id === "" && (
									<Tooltip title={t("tooltip.serverRequired")} placement='left'>
										<FontAwesomeIcon icon={faCircleInfo} className='generalStyles__infoTooltipIcon' />
									</Tooltip>
								)}
							</div>
						</div>
					</div>
				</div>
				{/** ************************************************************* */}
				{/** DIVIDER ASSIGN ADAPTER */}
				{channelType === "ADAPTER" && formAction === "EDIT" && (
					<>
						<br />
						<AdaptersAssignAdapter
							liftType={bank.type}
							setFloorsAndDirectionData={setFloorsAndDirectionData}
							formAction='EDIT'
							adapterId={bank.adapter_id || ""}
							lifts={selectedBank.number_of_lifts}
							stops={selectedBank.stops}
							maxStops={selectedBank.stops}
							floorsBelowLobby={selectedBank.floor_below_lobby}
						/>
					</>
				)}
				{/** ************************************************************* */}
				{/** DIVIDER ASSIGN CONFIGURATION */}
				{formAction === "CREATE" && (
					<>
						{(channelType === "SERIAL" || channelType === "TCP") && bank.type !== "GPIO" && (
							<>
								<br />
								<ConfigurationFilesAssignConfiguration
									setDataBlocks={setDataBlocks}
									liftType={bank.type}
									setFloorsAndDirectionData={setFloorsAndDirectionData}
								/>
							</>
						)}
						{(channelType === "SERIAL" || channelType === "TCP") && bank.type === "GPIO" && (
							<>
								<br />
								<DrawerRowDivider icon={faGears} title={`${t("general.configuration")}`} />
								<div className={`drawer__box__${theme}`}>
									<Alert message={t("edgeManagement.banks.gpioNotAssignable")} type='warning' showIcon closable />
								</div>
							</>
						)}
						{/** ************************************************************* */}
						{/** DIVIDER ASSIGN ADAPTER */}
						{channelType === "ADAPTER" && (
							<>
								<br />
								<AdaptersAssignAdapter
									liftType={bank.type}
									setFloorsAndDirectionData={setFloorsAndDirectionData}
									formAction='CREATE'
									adapterId=''
									lifts={1}
									stops={2}
									maxStops={2}
									floorsBelowLobby={1}
								/>
							</>
						)}
						{/** ************************************************************* */}
						{/** DIVIDER ASSIGN ADAPTER OR CONFIGURATION */}
						{channelType === "" && (
							<>
								<br />
								<DrawerRowDivider icon={faGears} title={`${t("edgeManagement.banks.configurationAndAdapters")}`} />
								<div className={`drawer__box__${theme}`}>
									<Alert
										message={t("edgeManagement.banks.configurationAndAdaptersDescription")}
										type='warning'
										showIcon
										closable={false}
									/>
								</div>
							</>
						)}
					</>
				)}
				{/** ************************************************************* */}
				{/** DIVIDER BACNET CONFIGURATION */}
				{formAction === "EDIT" && (
					<>
						<br />
						<DrawerRowDivider icon={faFileCode} title='BACnet Configuration' />
						<div className={`drawer__box__${theme}`}>
							{/** CLEAR BACNET CONFIG SWITCH */}
							<div className='generalStyles__flex drawer__container mt-3'>
								<div>
									<p className='drawer__title'>Clear Bacnet Config</p>
								</div>
								<div className='generalStyles__width100 drawer__textContainer generalStyles__flex'>
									<Switch
										checked={bank.clear_bacnet_config || false}
										onChange={onChangeClearBacnetConfig}
										size='small'
									/>
									<div className='generalStyles__infoTooltipIconBlank' />
								</div>
							</div>
						</div>
						{!bank.clear_bacnet_config && (
							<>
								<br />
								<CdoBacnetPage setCdo={setBacnetConfig} cdoValue={bacnetConfig} bankId={bank.id} />
							</>
						)}
					</>
				)}
				{/** ************************************************************* */}
				{/** GLOBAL ALERT */}
				<GlobalAlert2
					isError={isErrorPostBank}
					isSuccess={isSuccessPostBank}
					requestType='POST'
					error={errorPostBank}
					name='Bank'
				/>
				<GlobalAlert2
					isError={isErrorPutBank}
					isSuccess={isSuccessPutBank}
					requestType='PUT'
					error={errorPutBank}
					name='Bank'
				/>
			</Spin>
		</Drawer>
	);
}

export default BanksCreateEditForm;
